import React, { useEffect, useState, useRef } from 'react';
import { ArrowLeft, ThumbsUp, MessageSquare, Eye, Star, CheckCheck, Heart, Send, Image as ImageIcon, Trash2, Bookmark } from 'lucide-react';
import { useToast } from './Toast';
// Topic interface
interface ForumTopicType {
  id: number;
  title: string;
  author: string;
  authorType: 'student' | 'teacher';
  category: string;
  topic?: string;
  replies: number;
  views: number;
  likes: number;
  time: string;
  content?: string;
  visibility: 'public' | 'private' | 'institution';
  selectedUsers?: string[];
  type: 'question' | 'other';
  bestAnswerId?: number;
  teacherApprovedId?: number;
  solved?: boolean;
  image?: string;
  likedBy?: string[];
  mostLikedAnswerId?: number;
  repliedBy?: string[];
  archived?: boolean;
}
interface ForumTopicProps {
  topic: ForumTopicType;
  onClose: () => void;
  onAddReply: (topicId: number, replyContent: string, replyToId?: number) => void;
  onUpdateBestAnswer?: (topicId: number, answerId: number) => void;
  onUpdateTeacherApproved?: (topicId: number, answerId: number) => void;
  onUpdateMostLiked?: (topicId: number, answerId: number) => void;
  onArchive?: (topicId: number) => void;
}
export const ForumTopic: React.FC<ForumTopicProps> = ({
  topic,
  onClose,
  onAddReply,
  onUpdateBestAnswer,
  onUpdateTeacherApproved,
  onUpdateMostLiked,
  onArchive
}) => {
  // Mock toast function if useToast hook is not available
  const {
    toast = (message: string) => console.log(message)
  } = useToast ? useToast() : {};
  const [replyContent, setReplyContent] = useState('');
  const [replyImage, setReplyImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  // Sample replies
  const [replies, setReplies] = useState<any[]>([{
    id: 1,
    author: 'Zeynep Öğretmen',
    authorType: 'teacher',
    content: 'Bu soruyu çözerken, üslü sayıların özelliklerini doğru kullanmanız önemli. Negatif kuvvetlerin işlem sırasını ve paydaya geçiş kurallarını hatırlayın.',
    time: '2 saat önce',
    likes: 15,
    likedBy: [],
    isTeacherApproved: topic.teacherApprovedId === 1,
    isBestAnswer: topic.bestAnswerId === 1
  }, {
    id: 2,
    author: 'Mehmet K.',
    authorType: 'student',
    content: 'Ben de benzer bir soruyla karşılaşmıştım. Şu şekilde çözdüm: Öncelikle tüm üsleri pozitif yaparak başladım.',
    time: '1 saat önce',
    likes: 8,
    likedBy: ['currentUser'],
    isTeacherApproved: topic.teacherApprovedId === 2,
    isBestAnswer: topic.bestAnswerId === 2
  }]);
  // Handle image selection
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setReplyImage(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  // Clear image
  const clearImage = () => {
    setReplyImage(null);
    setImagePreview(null);
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };
  // Submit reply
  const handleSubmitReply = (e: React.FormEvent) => {
    e.preventDefault();
    if (replyContent.trim()) {
      const newReply = {
        id: Date.now(),
        author: 'Öğretmen',
        authorType: 'teacher',
        content: replyContent,
        time: 'Şimdi',
        likes: 0,
        likedBy: [],
        image: imagePreview,
        isTeacherApproved: false,
        isBestAnswer: false
      };
      setReplies([...replies, newReply]);
      onAddReply(topic.id, replyContent);
      // Clear form
      setReplyContent('');
      clearImage();
    }
  };
  // Like a reply
  const handleLikeReply = (replyId: number) => {
    setReplies(replies.map(reply => {
      if (reply.id === replyId) {
        const isLiked = reply.likedBy.includes('currentUser');
        return {
          ...reply,
          likes: isLiked ? reply.likes - 1 : reply.likes + 1,
          likedBy: isLiked ? reply.likedBy.filter((user: string) => user !== 'currentUser') : [...reply.likedBy, 'currentUser']
        };
      }
      return reply;
    }));
  };
  // Set as best answer
  const handleSetBestAnswer = (replyId: number) => {
    if (onUpdateBestAnswer) {
      onUpdateBestAnswer(topic.id, replyId);
      setReplies(replies.map(reply => ({
        ...reply,
        isBestAnswer: reply.id === replyId ? !reply.isBestAnswer : false
      })));
    }
  };
  // Set as teacher approved
  const handleSetTeacherApproved = (replyId: number) => {
    if (onUpdateTeacherApproved) {
      onUpdateTeacherApproved(topic.id, replyId);
      setReplies(replies.map(reply => ({
        ...reply,
        isTeacherApproved: reply.id === replyId ? !reply.isTeacherApproved : false
      })));
    }
  };
  // Delete reply (Teacher specific functionality)
  const handleDeleteReply = (replyId: number) => {
    setReplies(replies.filter(reply => reply.id !== replyId));
    toast('Yanıt başarıyla silindi!');
  };
  // Archive topic
  const handleArchiveTopic = () => {
    if (onArchive) {
      onArchive(topic.id);
    }
  };
  return <div className="p-4 space-y-6" data-id="element-875">
      {/* Header with back button */}
      <div className="flex items-center" data-id="element-876">
        <button onClick={onClose} className="mr-3 p-2 bg-gray-800 rounded-full hover:bg-gray-700 transition-colors" data-id="element-877">
          <ArrowLeft className="w-5 h-5" data-id="element-878" />
        </button>
        <h1 className="text-xl font-bold" data-id="element-879">Konu Detayı</h1>
      </div>
      {/* Main topic */}
      <div className="relative p-[1px] rounded-lg overflow-hidden bg-gradient-to-r from-emerald-600 to-teal-600" data-id="element-880">
        <div className="bg-black rounded-lg p-6" data-id="element-881">
          <div className="flex justify-between items-start" data-id="element-882">
            <div className="space-y-2" data-id="element-883">
              <h2 className="text-xl font-semibold" data-id="element-884">{topic.title}</h2>
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400" data-id="element-885">
                <div className="flex items-center" data-id="element-886">
                  <span className={`mr-1 ${topic.authorType === 'teacher' ? 'text-emerald-400' : ''}`} data-id="element-887">
                    {topic.author}
                  </span>
                  {topic.authorType === 'teacher' && <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full" data-id="element-888">
                      Öğretmen
                    </span>}
                </div>
                <div className="flex items-center" data-id="element-889">
                  <MessageSquare className="w-4 h-4 mr-1" data-id="element-890" />
                  <span data-id="element-891">{topic.replies} yanıt</span>
                </div>
                <div className="flex items-center" data-id="element-892">
                  <Eye className="w-4 h-4 mr-1" data-id="element-893" />
                  <span data-id="element-894">{topic.views} görüntülenme</span>
                </div>
                <div className="flex items-center" data-id="element-895">
                  <ThumbsUp className="w-4 h-4 mr-1" data-id="element-896" />
                  <span data-id="element-897">{topic.likes} beğeni</span>
                </div>
                <span data-id="element-898">{topic.time}</span>
              </div>
            </div>
            <div className="flex space-x-2" data-id="element-899">
              {/* Archive button */}
              <button onClick={handleArchiveTopic} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-emerald-400 transition-colors" title="Arşivle" data-id="element-900">
                <Bookmark className="w-5 h-5" data-id="element-901" />
              </button>
            </div>
          </div>
          {/* Topic content */}
          <div className="mt-4 border-t border-gray-800 pt-4" data-id="element-902">
            <p className="text-gray-300 whitespace-pre-line" data-id="element-903">{topic.content}</p>
            {topic.image && <div className="mt-4" data-id="element-904">
                <img src={topic.image} alt="Konu görseli" className="max-w-full max-h-96 rounded-lg object-contain" data-id="element-905" />
              </div>}
          </div>
        </div>
      </div>
      {/* Replies */}
      <div className="space-y-4" data-id="element-906">
        <h3 className="text-lg font-medium flex items-center" data-id="element-907">
          <MessageSquare className="w-5 h-5 mr-2 text-emerald-400" data-id="element-908" />
          Yanıtlar
        </h3>
        {replies.map(reply => <div key={reply.id} className={`relative p-[1px] rounded-lg overflow-hidden ${reply.isTeacherApproved ? 'bg-gradient-to-r from-emerald-600 to-teal-600' : reply.isBestAnswer ? 'bg-gradient-to-r from-amber-500 to-yellow-500' : reply.authorType === 'teacher' ? 'bg-gradient-to-r from-emerald-600/50 to-teal-600/50' : 'bg-gradient-to-r from-gray-700/50 to-gray-600/50'}`} data-id="element-909">
            <div className="bg-black rounded-lg p-4" data-id="element-910">
              {/* Reply header badges */}
              {reply.isTeacherApproved && <div className="absolute top-0 right-0 bg-emerald-600 text-white text-xs px-3 py-1 rounded-bl-lg" data-id="element-911">
                  <div className="flex items-center" data-id="element-912">
                    <CheckCheck className="w-3 h-3 mr-1" data-id="element-913" />
                    Öğretmen Onaylı
                  </div>
                </div>}
              {reply.isBestAnswer && !reply.isTeacherApproved && <div className="absolute top-0 right-0 bg-amber-500 text-white text-xs px-3 py-1 rounded-bl-lg" data-id="element-914">
                  <div className="flex items-center" data-id="element-915">
                    <Star className="w-3 h-3 mr-1" data-id="element-916" />
                    En İyi Cevap
                  </div>
                </div>}
              <div className="flex justify-between items-start" data-id="element-917">
                <div className="flex items-center" data-id="element-918">
                  <div className={`h-10 w-10 rounded-full ${reply.authorType === 'teacher' ? 'bg-emerald-900/40 text-emerald-400' : 'bg-gray-800 text-gray-300'} flex items-center justify-center mr-3`} data-id="element-919">
                    {reply.author.charAt(0)}
                  </div>
                  <div data-id="element-920">
                    <h4 className={`font-medium ${reply.authorType === 'teacher' ? 'text-emerald-400' : 'text-white'}`} data-id="element-921">
                      {reply.author}
                    </h4>
                    <p className="text-xs text-gray-400" data-id="element-922">{reply.time}</p>
                  </div>
                </div>
                <div className="flex space-x-2" data-id="element-923">
                  {/* Teacher specific actions */}
                  <button onClick={() => handleSetTeacherApproved(reply.id)} className={`p-1.5 rounded-lg ${reply.isTeacherApproved ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/5 hover:bg-white/10 text-gray-400 hover:text-emerald-400'} transition-colors`} title="Öğretmen Onaylı Olarak İşaretle" data-id="element-924">
                    <CheckCheck className="w-4 h-4" data-id="element-925" />
                  </button>
                  <button onClick={() => handleSetBestAnswer(reply.id)} className={`p-1.5 rounded-lg ${reply.isBestAnswer ? 'bg-amber-500/20 text-amber-400' : 'bg-white/5 hover:bg-white/10 text-gray-400 hover:text-amber-400'} transition-colors`} title="En İyi Cevap Olarak İşaretle" data-id="element-926">
                    <Star className="w-4 h-4" data-id="element-927" />
                  </button>
                  {/* TEACHER SPECIFIC: Delete button */}
                  <button onClick={() => handleDeleteReply(reply.id)} className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-red-400 transition-colors" title="Yanıtı Sil" data-id="element-928">
                    <Trash2 className="w-4 h-4" data-id="element-929" />
                  </button>
                </div>
              </div>
              <div className="mt-3 pl-13" data-id="element-930">
                <p className="text-gray-300 whitespace-pre-line" data-id="element-931">
                  {reply.content}
                </p>
                {reply.image && <div className="mt-3" data-id="element-932">
                    <img src={reply.image} alt="Yanıt görseli" className="max-w-full max-h-80 rounded-lg object-contain" data-id="element-933" />
                  </div>}
                <div className="mt-3 flex items-center space-x-4" data-id="element-934">
                  <button className={`flex items-center ${reply.likedBy.includes('currentUser') ? 'text-emerald-400' : 'text-gray-400 hover:text-emerald-400'} transition-colors`} onClick={() => handleLikeReply(reply.id)} data-id="element-935">
                    <ThumbsUp className="w-4 h-4 mr-1" data-id="element-936" />
                    <span className="text-sm" data-id="element-937">{reply.likes}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>)}
      </div>
      {/* Reply form */}
      <div className="relative p-[1px] rounded-lg overflow-hidden bg-gradient-to-r from-emerald-600/50 to-teal-600/50" data-id="element-938">
        <div className="bg-black rounded-lg p-4" data-id="element-939">
          <h3 className="text-lg font-medium mb-3" data-id="element-940">Yanıt Yaz</h3>
          <form onSubmit={handleSubmitReply} data-id="element-941">
            <div className="relative p-[1px] rounded-lg overflow-hidden bg-gradient-to-r from-emerald-600/40 to-teal-600/40" data-id="element-942">
              <textarea value={replyContent} onChange={e => setReplyContent(e.target.value)} placeholder="Yanıtınızı buraya yazın..." rows={4} className="w-full bg-gray-900 rounded-lg py-2 px-3 focus:outline-none resize-none" required data-id="element-943"></textarea>
            </div>
            {/* Image preview */}
            {imagePreview && <div className="mt-3 relative" data-id="element-944">
                <img src={imagePreview} alt="Önizleme" className="max-h-60 rounded-lg object-contain" data-id="element-945" />
                <button type="button" onClick={clearImage} className="absolute top-2 right-2 p-1 bg-black/70 rounded-full" data-id="element-946">
                  <Trash2 className="w-4 h-4 text-red-400" data-id="element-947" />
                </button>
              </div>}
            <div className="mt-3 flex justify-between items-center" data-id="element-948">
              <div data-id="element-949">
                <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" ref={imageInputRef} data-id="element-950" />
                <button type="button" onClick={() => imageInputRef.current?.click()} className="bg-gray-800 hover:bg-gray-700 text-gray-300 px-3 py-2 rounded-lg flex items-center transition-colors" data-id="element-951">
                  <ImageIcon className="w-4 h-4 mr-2" data-id="element-952" />
                  Görsel Ekle
                </button>
              </div>
              <button type="submit" className="bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-lg flex items-center transition-colors" data-id="element-953">
                <Send className="w-4 h-4 mr-2" data-id="element-954" />
                Yanıt Gönder
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>;
};